// What to purge from browser storage when a clinician signs out.
//
// Amplify clears its own Cognito tokens on signOut(), and sessionStorage dies
// with the tab. Everything the PORTAL puts in localStorage, however, survives
// sign-out and the next person to use the browser can read it -- on a shared
// clinic workstation that is a real exposure, because these values name
// patients and cases.
//
// The list is expressed as predicates rather than a flat array because several
// keys are per-user or per-name and cannot be enumerated ahead of time
// (`clinicianPrefs:<clinicianId>`, `searchHistory_<tableName>`).
//
// IMPORTANT -- one key must SURVIVE sign-out:
//   percix_device_id  MFA device trust. Clearing it re-challenges the clinician
//                     for a fresh MFA code on every login, which is both a
//                     support burden and a nudge toward MFA fatigue. It is a
//                     random opaque id and names no patient.
// `measurementSystem` (cm vs in) is also kept: a display unit preference that
// reveals nothing about anyone.

// Exact keys to remove.
const EXACT_KEYS_TO_CLEAR = [
  // Whether the inactivity-timeout modal was showing.
  'session_timeout_modal',
  // Inactivity clock. Stale value from a previous user is meaningless and, if
  // old enough, pops the timeout modal immediately on the next login.
  'last_activity_time',
  // The last live session id looked at on the assessments screen.
  'sessionId',
  // Per-table column sizing. TableView keys these by its `tableId` prop; the
  // two live ids are below. Harmless in content but user-specific.
  'PatientsTable',
  'CliniciansTable',
];

// Prefixed / templated keys to remove.
const KEY_PREFIXES_TO_CLEAR = [
  // clinicianPrefs:<clinicianId> -- per-clinician view preferences, and it
  // holds lastCaseIdByPatient, i.e. patient AND case identifiers.
  'clinicianPrefs:',
  // searchHistory_<tableName> -- literally what the previous clinician typed
  // into a patient search box. The most directly identifying of the set.
  'searchHistory_',
];

// Keys that must be PRESERVED. Listed explicitly so the intent is testable and
// a future edit to the rules above cannot silently start clearing them.
export const KEYS_PRESERVED_ON_SIGN_OUT = ['percix_device_id', 'measurementSystem'];

export function shouldClearOnSignOut(key: string): boolean {
  if (KEYS_PRESERVED_ON_SIGN_OUT.indexOf(key) !== -1) {
    return false;
  }
  if (EXACT_KEYS_TO_CLEAR.indexOf(key) !== -1) {
    return true;
  }
  return KEY_PREFIXES_TO_CLEAR.some((prefix) => key.startsWith(prefix));
}

// Remove every matching key from localStorage.
//
// Enumerates the store rather than removing a fixed list, so per-user and
// per-name keys are caught without knowing the clinician id or table name.
// Collects first, then removes: mutating localStorage while indexing over it
// shifts the indices and silently skips keys.
export function clearPortalStorageOnSignOut(
  storage: Pick<Storage, 'length' | 'key' | 'removeItem'> = localStorage,
): string[] {
  const doomed: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    if (key !== null && shouldClearOnSignOut(key)) {
      doomed.push(key);
    }
  }
  doomed.forEach((key) => storage.removeItem(key));
  return doomed;
}
